import React, { KeyboardEvent, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { StylishNewButton as Button } from 'components/DesignSystems/New/StylishNewButton';
import { useInlineEditableField } from 'components/GeoLocationComponent/useInlineEditableField';

export type InlineEditableTextProps = {
  label: string;
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  readOnly?: boolean;
};

export const InlineEditableText: React.FC<InlineEditableTextProps> = ({
  label,
  value,
  onSave,
  placeholder,
  multiline = false,
  readOnly = false,
}) => {
  const { isEditing, draft, beginEdit, updateDraft, save, cancel } =
    useInlineEditableField(value, onSave);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      save();
    } else if (e.key === 'Escape') {
      cancel();
    }
  };

  const handleMouseDown = (e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    action();
  };

  return (
    <div className="d-flex align-items-center flex-wrap tw-mb-3">
      <span className="fst-italic me-2">{label}:</span>
      {isEditing ? (
        <div className="d-flex align-items-center gap-2">
          {multiline ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              className="form-control form-control-sm"
              value={draft}
              onChange={(e) => updateDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              onBlur={save}
              data-testid={`inline-edit-input-${label}`}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              className="form-control form-control-sm"
              value={draft}
              onChange={(e) => updateDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              onBlur={save}
              data-testid={`inline-edit-input-${label}`}
            />
          )}
          <Button
            type="button"
            onMouseDown={(e) => handleMouseDown(e, save)}
            onClick={save}
          >
            Accept {label} Change
          </Button>
          <Button
            type="button"
            onMouseDown={(e) => handleMouseDown(e, cancel)}
            onClick={cancel}
          >
            Cancel {label} Change
          </Button>
        </div>
      ) : (
        <span
          role="presentation"
          onClick={() => {
            if (!readOnly) {
              beginEdit();
            }
          }}
          className={classNames('fw-bold', {
            'geolocation-component-editable': !readOnly,
          })}
          data-testid={`inline-edit-text-${label}`}
        >
          {value || ''}
        </span>
      )}
    </div>
  );
};
